import { useEffect } from "react";
import { BlogPostFrontmatter } from "../types/mdx";

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  url: string;
  image?: string;
  type?: "website" | "article";
  frontmatter?: BlogPostFrontmatter;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  author,
  publishedTime,
  modifiedTime,
  url,
  image,
  type = "article",
  frontmatter,
}) => {
  const siteName = "Pulse Player Blog";
  const siteUrl = window.location.origin;
  const fullUrl = `${siteUrl}${url}`;

  // Default image if none provided
  const defaultImage = `${siteUrl}/logo+bg.png`;
  const metaImage = image || defaultImage;

  // Generate structured data for article
  const generateStructuredData = () => {
    if (type === "article" && frontmatter) {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description: description || frontmatter.excerpt,
        image: metaImage,
        author: {
          "@type": "Person",
          name: author || frontmatter.author || "Pulse Player Team",
        },
        publisher: {
          "@type": "Organization",
          name: siteName,
          logo: {
            "@type": "ImageObject",
            url: defaultImage,
          },
        },
        datePublished: publishedTime || frontmatter.date,
        dateModified: modifiedTime || frontmatter.date,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": fullUrl,
        },
        keywords: keywords?.join(", ") || frontmatter.tags?.join(", "),
      };
    }
    return null;
  };

  useEffect(() => {
    // Update document title
    document.title = `${title} | ${siteName}`;

    // Remove existing meta tags that we'll be replacing
    const existingMetas = document.querySelectorAll('meta[data-seo="true"]');
    existingMetas.forEach((meta) => meta.remove());

    // Remove existing structured data
    const existingStructuredData = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    // Helper function to create meta tag
    const createMetaTag = (attributes: Record<string, string>) => {
      const meta = document.createElement("meta");
      Object.entries(attributes).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      meta.setAttribute("data-seo", "true");
      return meta;
    };

    // Create meta tags array
    const metaTags = [
      // Basic meta tags
      {
        name: "description",
        content: description || "Discover the latest in haptic music technology and immersive audio experiences.",
      },
      {
        name: "keywords",
        content:
          keywords?.join(", ") || frontmatter?.tags?.join(", ") || "haptic music, audio technology, immersive audio",
      },
      { name: "author", content: author || frontmatter?.author || "Pulse Player Team" },

      // Open Graph tags
      { property: "og:title", content: title },
      {
        property: "og:description",
        content:
          description ||
          frontmatter?.excerpt ||
          "Discover the latest in haptic music technology and immersive audio experiences.",
      },
      { property: "og:type", content: type },
      { property: "og:url", content: fullUrl },
      { property: "og:image", content: metaImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:site_name", content: siteName },
      { property: "og:locale", content: "en_US" },

      // Twitter Card tags
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      {
        name: "twitter:description",
        content:
          description ||
          frontmatter?.excerpt ||
          "Discover the latest in haptic music technology and immersive audio experiences.",
      },
      { name: "twitter:image", content: metaImage },
      { name: "twitter:site", content: "@pulseplayer" },
      {
        name: "twitter:creator",
        content: `@${(author || frontmatter?.author || "pulseplayer").toLowerCase().replace(/\s+/g, "")}`,
      },

      // Article-specific tags
      ...(type === "article" && publishedTime
        ? [
            { property: "article:published_time", content: publishedTime },
            { property: "article:modified_time", content: modifiedTime || publishedTime },
            { property: "article:author", content: author || frontmatter?.author || "Pulse Player Team" },
            { property: "article:section", content: "Technology" },
            ...(frontmatter?.tags || []).map((tag) => ({ property: "article:tag", content: tag })),
          ]
        : []),

      // Additional SEO tags
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow" },
      { name: "bingbot", content: "index, follow" },
      { name: "theme-color", content: "#1e293b" },
      { name: "msapplication-TileColor", content: "#1e293b" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "format-detection", content: "telephone=no" },
    ];

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = fullUrl;

    // Add meta tags to head
    const head = document.head;
    metaTags.forEach((tagData) => {
      const meta = createMetaTag(tagData);
      head.appendChild(meta);
    });

    // Add structured data
    const structuredData = generateStructuredData();
    if (structuredData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "true");
      script.textContent = JSON.stringify(structuredData);
      head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Reset title to default
      document.title = siteName;

      // Remove our meta tags
      const seoMetas = document.querySelectorAll('meta[data-seo="true"]');
      seoMetas.forEach((meta) => meta.remove());

      // Remove structured data
      const seoStructuredData = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
      if (seoStructuredData) {
        seoStructuredData.remove();
      }
    };
  }, [title, description, keywords, author, publishedTime, modifiedTime, fullUrl, metaImage, type, frontmatter]);

  return null; // This component doesn't render anything
};

export default SEO;
